import type { TableMetric } from "@v2/features/analytics/constants";
import type { CurrencyCode } from "schemas/project";

import * as React from "react";

import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import { METRICS_ALLOWING_ZERO_VALUE_WHEN_NO_DATA } from "@v2/features/analytics/constants";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import useCurrentAnalyticsCurrencyCode from "@v2/features/analytics/hooks/useCurrentAnalyticsCurrencyCode";
import { formatValueToTableCell } from "@v2/features/analytics/utils/formatMetric";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { formatWithCommasAndTwoDecimals } from "replo-utils/lib/math";

const MetricWithDelta: React.FC<{
  name: TableMetric;
  value: number | null;
  delta?: number;
  doesCompareValueExist: boolean;
  wrapperClassName?: string;
  valueClassName?: string;
  tagClassName?: string;
  touchType?: string;
  isFractional?: boolean;
  currency?: CurrencyCode;
  dataDisplayType?: string;
}> = ({
  name,
  value,
  delta,
  doesCompareValueExist,
  wrapperClassName,
  valueClassName,
  tagClassName,
  touchType,
  isFractional,
  currency: currencyProp,
  dataDisplayType: dataDisplayTypeProp,
}) => {
  const { query } = useAnalyticsQueryContext();
  const currencyCode = useCurrentAnalyticsCurrencyCode();
  const { dataDisplayType: dataDisplayTypeFromStatus } = useAnalyticsStatus();
  const dataDisplayType = dataDisplayTypeProp ?? dataDisplayTypeFromStatus;
  const currency = currencyProp ?? currencyCode;
  const getVariant = (() => {
    if (!delta || !doesCompareValueExist) {
      return "neutral";
    }
    return delta > 0 ? "success" : "danger";
  })();

  const getIcon = (() => {
    if (!doesCompareValueExist) {
      return <Minus />;
    }
    if (delta && delta !== 0) {
      return delta > 0 ? <ArrowUp /> : <ArrowDown />;
    }
  })();

  return (
    <div
      className={twMerge(
        "justify-left inline-flex items-center gap-3 whitespace-nowrap",
        wrapperClassName,
      )}
    >
      <div className={twMerge("min-w-[10px]", valueClassName)}>
        {/* NOTE (Max, 2025-06-13): For some metrics, in the case where there's no data, we
        want to show 0 instead of a minus sign (such as conversions, revenue). However, for other
        metrics like conversion_rates or AOV, they're technically not 0 if there's no data (as they are
        rates). Instead, they're undefined, so we show minus sign. */}
        {value !== null ||
        METRICS_ALLOWING_ZERO_VALUE_WHEN_NO_DATA.includes(name) ? (
          formatValueToTableCell({
            value: value ?? 0,
            name,
            currency,
            touchType,
            isFractional: isFractional ?? query.isFractional,
          })
        ) : (
          <Minus size={14} />
        )}
      </div>
      {delta !== undefined && (
        <Tag
          size="sm"
          variant={getVariant}
          UNSAFE_className={twMerge(
            tagClassName,
            dataDisplayType === "partialData" && "blur-xs",
          )}
        >
          {getIcon}
          {doesCompareValueExist &&
            `${formatWithCommasAndTwoDecimals(Math.abs(delta), true)}%`}
        </Tag>
      )}
    </div>
  );
};

export default MetricWithDelta;
